/*
 * Search, the phone tab in place of Clients (owner, 2026-10-30: "remove clients and add search instead", like Jobber's): one
 * box over clients, requests, quotes, jobs and invoices. With nothing picked it shows what was opened lately on this phone,
 * then the newest of each kind together. Tap a kind for its list and its status chips (Active, Past due and the rest); the
 * box then searches that kind only. Each row shows the date, the money and what it is for, with its status on the right.
 */
import {
  INVOICE_STATE_WORDS,
  QUOTE_STATUS_WORDS,
  REQUEST_STATE_WORDS,
  type ClientRow,
  type InvoiceRow,
  type InvoiceState,
  type JobRow,
  type QuoteRow,
  type QuoteStatus,
  type RequestRow,
  type RequestState,
} from "@steward/shared";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useClients, useJobs } from "../api/hooks";
import { useInvoices, useQuotes, useRequests } from "../api/money";
import { useSession } from "../app/session";
import { money, shortDate } from "../components/format";
import { Icon, type IconName } from "../components/Icon";
import { LoadError, Skeleton, StatusLabel, type Status } from "../components/parts";

type Kind = "clients" | "requests" | "quotes" | "jobs" | "invoices";

interface Kinds {
  label: string;
  one: string;
  icon: IconName;
  action: Parameters<ReturnType<typeof useSession>["can"]>[0];
  /** The full list page, for everything past what Search shows. */
  to: "/clients" | "/requests" | "/quotes" | "/jobs" | "/invoices";
  filters: { key: string; label: string }[];
}

const KINDS: Record<Kind, Kinds> = {
  clients: {
    label: "Clients",
    one: "clients and properties",
    icon: "clients",
    action: "client.read",
    to: "/clients",
    filters: [
      { key: "active", label: "Active" },
      { key: "nothing_booked", label: "Nothing booked" },
      { key: "at_risk", label: "At risk" },
      { key: "inactive", label: "Archived" },
    ],
  },
  requests: {
    label: "Requests",
    one: "requests",
    icon: "requests",
    action: "lead.read",
    to: "/requests",
    filters: (["new", "sent", "converted", "lost"] as const).map((s) => ({ key: s, label: REQUEST_STATE_WORDS[s] })),
  },
  quotes: {
    label: "Quotes",
    one: "quotes",
    icon: "quotes",
    action: "quote.read",
    to: "/quotes",
    filters: (["draft", "sent", "approved", "converted", "declined"] as const).map((s) => ({
      key: s,
      label: QUOTE_STATUS_WORDS[s],
    })),
  },
  jobs: {
    label: "Jobs",
    one: "jobs",
    icon: "jobs",
    action: "job.read",
    to: "/jobs",
    filters: [
      { key: "repeating", label: "Repeating" },
      { key: "one-time", label: "One time" },
      { key: "unscheduled", label: "Nothing booked" },
      { key: "ended", label: "Ended" },
    ],
  },
  invoices: {
    label: "Invoices",
    one: "invoices",
    icon: "invoices",
    action: "invoice.read",
    to: "/invoices",
    filters: (["to_check", "awaiting", "past_due", "paid"] as const).map((s) => ({ key: s, label: INVOICE_STATE_WORDS[s] })),
  },
};
const ORDER: Kind[] = ["clients", "requests", "quotes", "jobs", "invoices"];
/** How many of each kind the mixed list takes before sorting them together. */
const EACH = 8;

/** One line in the list, whatever it is. */
interface Hit {
  kind: Kind;
  id: string;
  title: string;
  /** The day shown, YYYY-MM-DD. */
  date: string;
  /** The day it sorts by when kinds mix: when it last moved, never a day still to come. */
  sort?: string;
  bits: string[];
  status: { look: Status; words: string } | null;
}

const INVOICE_LOOK: Record<InvoiceState, Status> = {
  to_check: "informative",
  awaiting: "warning",
  past_due: "critical",
  debt: "critical",
  paid: "success",
  closed: "inactive",
};
const QUOTE_LOOK: Record<QuoteStatus, Status> = {
  draft: "inactive",
  sent: "warning",
  approved: "success",
  converted: "informative",
  declined: "critical",
  expired: "inactive",
};
const REQUEST_LOOK: Record<RequestState, Status> = {
  new: "informative",
  sent: "warning",
  converted: "success",
  lost: "inactive",
};

const day = (s: string | null | undefined) => (s ?? "").slice(0, 10);

function clientHit(c: ClientRow): Hit {
  return {
    kind: "clients",
    id: c.id,
    title: c.name,
    date: day(c.lastActivity?.at ?? c.createdAt),
    bits: [c.address ?? c.phone ?? ""],
    status: !c.active ? { look: "inactive", words: "Archived" } : c.atRisk ? { look: "warning", words: "At risk" } : null,
  };
}
function requestHit(r: RequestRow): Hit {
  return {
    kind: "requests",
    id: r.id,
    title: r.name,
    date: day(r.createdAt),
    bits: [r.valueCents ? money(r.valueCents) : "", r.service ?? ""],
    status:
      r.archived && r.state !== "converted" && r.state !== "lost"
        ? { look: "inactive", words: "Archived" }
        : { look: REQUEST_LOOK[r.state], words: REQUEST_STATE_WORDS[r.state] },
  };
}
function quoteHit(q: QuoteRow): Hit {
  return {
    kind: "quotes",
    id: q.id,
    title: q.name,
    date: day(q.sentAt ?? q.createdAt),
    bits: [money(q.totalCents), q.title ?? `Quote #${String(q.number)}`],
    status: { look: QUOTE_LOOK[q.status], words: QUOTE_STATUS_WORDS[q.status] },
  };
}
function jobHit(j: JobRow, today: string): Hit {
  const late = j.active && j.nextVisit !== null && j.nextVisit < today;
  return {
    kind: "jobs",
    id: j.id,
    title: j.clientName,
    date: day(j.nextVisit ?? j.startDate),
    sort: [day(j.startDate), today].sort()[0] ?? today,
    bits: [j.visitTotalCents ? money(j.visitTotalCents) : "", j.name],
    status: !j.active
      ? { look: "inactive", words: "Ended" }
      : late
        ? { look: "critical", words: "Late" }
        : j.nextVisit
          ? { look: "informative", words: "Upcoming" }
          : { look: "warning", words: "Nothing booked" },
  };
}
function invoiceHit(i: InvoiceRow): Hit {
  return {
    kind: "invoices",
    id: i.id,
    title: i.clientName,
    date: day(i.issuedOn ?? i.firstServiceDate),
    bits: [money(i.totalCents), i.subject],
    status: { look: INVOICE_LOOK[i.state], words: INVOICE_STATE_WORDS[i.state] },
  };
}

/** Matches the typed words anywhere in the name or the line under it (the server already did this for some kinds). */
const matches = (h: Hit, q: string) => !q || `${h.title} ${h.bits.join(" ")}`.toLowerCase().includes(q.toLowerCase());

const RECENT_KEY = "steward.search.recent";
const RECENT_MAX = 6;
function readRecent(): Hit[] {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as unknown;
    return Array.isArray(list) ? (list as Hit[]).filter((h) => ORDER.includes(h.kind)) : [];
  } catch {
    return [];
  }
}

export function SearchPhone() {
  const { company, can } = useSession();
  const [kind, setKind] = useState<Kind | null>(null);
  const [filter, setFilter] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const [recent, setRecent] = useState(readRecent);
  const chips = useRef<HTMLDivElement>(null);
  // A kind picked far along the row starts its status chips from the left.
  useEffect(() => {
    if (chips.current) chips.current.scrollLeft = 0;
  }, [kind]);
  const q = useDebounced(typed.trim(), 250);
  const kinds = ORDER.filter((k) => can(KINDS[k].action));
  const want = (k: Kind) => can(KINDS[k].action) && (kind === null || kind === k);
  const f = filter ?? "";

  const clients = useClients(
    company.id,
    {
      q: q || undefined,
      status: kind === "clients" && f === "inactive" ? "inactive" : kind === "clients" ? "active" : "all",
      sort: q ? "name" : "last_activity",
      show: kind === "clients" && (f === "nothing_booked" || f === "at_risk") ? f : undefined,
    },
    want("clients"),
  );
  const jobs = useJobs(
    company.id,
    {
      q: q || undefined,
      status: kind === "jobs" && f === "ended" ? "ended" : kind === "jobs" && f ? "active" : "all",
      kind: kind === "jobs" && (f === "repeating" || f === "one-time") ? f : "all",
    },
    want("jobs"),
  );
  const invoices = useInvoices(
    company.id,
    { state: kind === "invoices" && f ? (f as InvoiceState) : "all", q: q || undefined },
    want("invoices"),
  );
  const quotes = useQuotes(company.id, kind === "quotes" && f ? f : "all", want("quotes"));
  const requests = useRequests(company.id, want("requests"));

  const today = new Date().toISOString().slice(0, 10);
  const lists: Record<Kind, { hits: Hit[] | undefined; error: unknown; retry: () => unknown }> = {
    clients: { hits: clients.data?.items.map(clientHit), error: clients.error, retry: clients.refetch },
    jobs: {
      hits: jobs.data?.items
        .map((j) => jobHit(j, today))
        .filter((h) => kind !== "jobs" || f !== "unscheduled" || h.status?.words === "Nothing booked"),
      error: jobs.error,
      retry: jobs.refetch,
    },
    invoices: { hits: invoices.data?.invoices.map(invoiceHit), error: invoices.error, retry: invoices.refetch },
    quotes: { hits: quotes.data?.quotes.map(quoteHit), error: quotes.error, retry: quotes.refetch },
    requests: {
      hits: requests.data?.requests.filter((r) => kind !== "requests" || !f || r.state === f).map(requestHit),
      error: requests.error,
      retry: requests.refetch,
    },
  };
  const shown = kind ? [kind] : kinds;
  const failed = shown.find((k) => lists[k].error);
  const loading = shown.some((k) => !lists[k].hits);
  const newest = (h: Hit[]) => [...h].sort((a, b) => (b.sort ?? b.date).localeCompare(a.sort ?? a.date));
  const hits = kind
    ? (lists[kind].hits ?? []).filter((h) => matches(h, q))
    : newest(shown.flatMap((k) => (lists[k].hits ?? []).filter((h) => matches(h, q)).slice(0, q ? EACH : EACH / 2)));

  const opened = (h: Hit) => {
    const next = [h, ...recent.filter((x) => x.kind !== h.kind || x.id !== h.id)].slice(0, RECENT_MAX);
    setRecent(next);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      // Kept until the page closes.
    }
  };
  const pick = (k: Kind | null) => {
    setKind(k);
    setFilter(null);
  };
  const heading = kind ? `${q ? "Found" : "Recent"} ${KINDS[kind].label.toLowerCase()}` : q ? "Found" : "Recently active";

  return (
    <div className="psearch">
      <h1 className="psearch-title">Search</h1>
      <label className="psearch-box">
        <Icon name="search" size={22} />
        <input
          type="search"
          enterKeyHint="search"
          autoComplete="off"
          placeholder={kind ? `Search ${KINDS[kind].one}` : "Search"}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
        {typed ? (
          <button type="button" className="psearch-clear" aria-label="Clear" onClick={() => setTyped("")}>
            <Icon name="closeCircle" size={20} />
          </button>
        ) : null}
      </label>
      <div className="psearch-chips" ref={chips} role="group" aria-label="What to search">
        {kind ? (
          <>
            <button
              type="button"
              className={`psearch-chip on k-${kind}`}
              aria-label={`${KINDS[kind].label}, tap to search everything`}
              onClick={() => pick(null)}
            >
              <Icon name={KINDS[kind].icon} size={20} />
              <span className="psearch-x">
                <Icon name="close" size={14} />
              </span>
            </button>
            {KINDS[kind].filters.map((x) => (
              <button
                key={x.key}
                type="button"
                className={`psearch-chip${filter === x.key ? " sel" : ""}`}
                aria-pressed={filter === x.key}
                onClick={() => setFilter(filter === x.key ? null : x.key)}
              >
                {x.label}
              </button>
            ))}
          </>
        ) : (
          kinds.map((k) => (
            <button key={k} type="button" className={`psearch-chip k-${k}`} onClick={() => pick(k)}>
              <Icon name={KINDS[k].icon} size={20} />
              {KINDS[k].label}
            </button>
          ))
        )}
      </div>
      {!kind && !q && recent.length ? (
        <section>
          <div className="psearch-head">
            <h2>Opened lately</h2>
            <button
              type="button"
              className="link"
              onClick={() => {
                setRecent([]);
                try {
                  localStorage.removeItem(RECENT_KEY);
                } catch {
                  // Nothing kept.
                }
              }}
            >
              Clear
            </button>
          </div>
          <HitList hits={recent} onOpen={opened} />
        </section>
      ) : null}
      <section>
        <div className="psearch-head">
          <h2>{heading}</h2>
          {kind ? (
            <Link to={KINDS[kind].to} className="link">
              See all
            </Link>
          ) : null}
        </div>
        {failed ? (
          <LoadError error={lists[failed].error} retry={() => void lists[failed].retry()} />
        ) : loading && !hits.length ? (
          <Skeleton rows={6} />
        ) : hits.length ? (
          <HitList hits={hits} onOpen={opened} />
        ) : (
          <p className="psearch-empty">{q ? `Nothing matches "${q}".` : "Nothing here yet."}</p>
        )}
      </section>
    </div>
  );
}

function HitList({ hits, onOpen }: { hits: Hit[]; onOpen: (h: Hit) => void }) {
  return (
    <ul className="psearch-list">
      {hits.map((h) => (
        <li key={`${h.kind}-${h.id}`}>
          <HitLink hit={h} onOpen={() => onOpen(h)}>
            <span className={`psearch-icon k-${h.kind}`}>
              <Icon name={KINDS[h.kind].icon} size={24} />
            </span>
            <span className="psearch-main">
              <b>{h.title}</b>
              <span className="psearch-sub">{[shortDate(h.date), ...h.bits].filter(Boolean).join("  |  ")}</span>
            </span>
            {h.status ? (
              <StatusLabel status={h.status.look}>
                <span className="psearch-status">{h.status.words}</span>
              </StatusLabel>
            ) : null}
          </HitLink>
        </li>
      ))}
    </ul>
  );
}

function HitLink({ hit, onOpen, children }: { hit: Hit; onOpen: () => void; children: ReactNode }) {
  const props = { className: "psearch-row", onClick: onOpen, params: { id: hit.id } };
  switch (hit.kind) {
    case "clients":
      return (
        <Link to="/clients/$id" {...props}>
          {children}
        </Link>
      );
    case "requests":
      return (
        <Link to="/requests/$id" {...props}>
          {children}
        </Link>
      );
    case "quotes":
      return (
        <Link to="/quotes/$id" {...props}>
          {children}
        </Link>
      );
    case "jobs":
      return (
        <Link to="/jobs/$id" {...props}>
          {children}
        </Link>
      );
    case "invoices":
      return (
        <Link to="/invoices/$id" {...props}>
          {children}
        </Link>
      );
  }
}

function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
